<template>
  <section class="dp-layout">
    <section class="dp-right log-page">
    <header class="log-page__head">
      <div>
        <h3 class="log-page__title">操作审计日志</h3>
        <p class="log-page__desc">写操作和失败响应保留有限正文；成功只读请求仅保留元数据；单次最长 {{ maxRangeDays }} 天</p>
      </div>
      <el-tooltip placement="bottom" effect="dark">
        <div slot="content">
          已入队 {{ queueHealth.Enqueued || 0 }}，已落库 {{ queueHealth.Persisted || 0 }}，<br />
          丢弃 {{ queueHealth.Dropped || 0 }}，失败 {{ queueHealth.Failed || 0 }}
        </div>
        <button type="button" class="queue-health" :class="queueHealthClass" @click="loadQueueHealth">
          <i class="el-icon-connection" />
          <span>写入队列 {{ queueHealth.Pending || 0 }}/{{ queueHealth.Capacity || '—' }}</span>
        </button>
      </el-tooltip>
    </header>

    <div class="toolbar-filter-bar">
      <el-form :inline="true" class="toolbar-filter-form" @submit.native.prevent>
        <div class="toolbar-filter-fields">
        <el-form-item label="范围">
          <el-radio-group v-model="filters.scope" @change="handleSearch">
            <el-radio-button label="write">写操作</el-radio-button>
            <el-radio-button label="all">全部请求</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="时间">
          <el-date-picker
            v-model="filters.dateRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            start-placeholder="开始"
            end-placeholder="结束"
            :default-time="['00:00:00', '23:59:59']"
            class="toolbar-field-w-xl"
          />
        </el-form-item>
        <el-form-item label="关键词">
          <el-input
            v-model="filters.keyword"
            clearable
            placeholder="姓名 / 编号 / ID / 摘要 / 接口 / TraceId"
            class="toolbar-field-w-lg"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item label="结果">
          <el-select v-model="filters.success" clearable placeholder="全部" class="toolbar-field-w-sm">
            <el-option label="成功" :value="true" />
            <el-option label="失败" :value="false" />
          </el-select>
        </el-form-item>
        <el-form-item label="耗时≥">
          <el-select v-model="filters.minDurationMs" clearable placeholder="全部" class="toolbar-field-w-sm">
            <el-option label="≥300ms" :value="300" />
            <el-option label="≥500ms" :value="500" />
            <el-option label="≥1秒" :value="1000" />
            <el-option label="≥3秒" :value="3000" />
            <el-option label="≥5秒" :value="5000" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </el-form-item>
        </div>
        <div class="toolbar-filter-actions">
          <el-button size="small" type="info" icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
        </div>
      </el-form>
    </div>

    <div class="tbl-scroll-host">
      <el-table
        :data="users"
        v-loading="listLoading"
        class="log-table custom-tbl"
        height="100%"
        stripe
        style="width: 100%"
        empty-text="当前条件下没有审计记录"
      >
        <el-table-column prop="StartTime" label="时间" width="158" />
        <el-table-column prop="DurationMs" label="耗时" width="88" align="right">
          <template slot-scope="scope">
            <span :class="durationClass(scope.row.DurationMs)">{{ scope.row.DurationMs }}ms</span>
          </template>
        </el-table-column>
        <el-table-column prop="UserName" label="操作人" width="100" show-overflow-tooltip />
        <el-table-column prop="HttpMethod" label="方法" width="88" align="center">
          <template slot-scope="scope">
            <span class="method-pill" :class="'method-pill--' + (scope.row.HttpMethod || '').toLowerCase()">
              {{ scope.row.HttpMethod || '—' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="Path" label="接口" min-width="280" show-overflow-tooltip>
          <template slot-scope="scope">
            <code class="mono-cell">{{ scope.row.Path || '—' }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="BizAction" label="业务动作" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.BizAction || scope.row.ActionName || '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="Success" label="结果" width="78" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.Success ? 'success' : 'danger'" size="mini" effect="plain">
              {{ scope.row.Success ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="showClientIp" prop="ClientIp" width="130">
          <template slot="header">
            <span>IP</span>
            <el-tooltip placement="top" effect="dark">
              <div slot="content" class="code-tip">
                客户端 IP（已规范化展示）。<br />
                点击 IP 可查看归属地：<br />
                本机/局域网直接提示；公网查询运营商与地区。
              </div>
              <i class="el-icon-info col-header-tip" />
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <a
              v-if="scope.row.ClientIp"
              href="javascript:;"
              class="ip-link"
              @click.stop="openIpLocation(scope.row.ClientIp)"
            >{{ scope.row.ClientIp }}</a>
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" align="center">
          <template slot-scope="scope">
            <el-button type="text" class="detail-link" @click="showDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-col :span="24" class="toolbar">
      <el-pagination
        layout="total, prev, pager, next, jumper"
        @current-change="handleCurrentChange"
        :page-size="pageSize"
        :total="total"
        style="float: right"
      />
    </el-col>

    <el-dialog
      :visible.sync="detailVisible"
      v-loading="detailLoading"
      width="780px"
      custom-class="log-detail-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <div slot="title" class="log-detail-dialog__title">
        <span>审计详情</span>
        <el-tag
          v-if="current"
          :type="current.Success ? 'success' : 'danger'"
          size="mini"
          effect="plain"
        >{{ current.Success ? '成功' : '失败' }}</el-tag>
      </div>

      <template v-if="current">
        <div class="meta-grid">
          <div class="meta-item">
            <div class="meta-item__label">时间</div>
            <div class="meta-item__value">{{ current.StartTime || '—' }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">操作人</div>
            <div class="meta-item__value">{{ current.UserName || '—' }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">方法</div>
            <div class="meta-item__value">
              <span class="method-pill" :class="'method-pill--' + (current.HttpMethod || '').toLowerCase()">
                {{ current.HttpMethod || '—' }}
              </span>
            </div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">耗时</div>
            <div class="meta-item__value" :class="durationClass(current.DurationMs)">{{ current.DurationMs }}ms</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">HTTP</div>
            <div class="meta-item__value" :class="statusClass(current.StatusCode)">{{ current.StatusCode }}</div>
          </div>
          <div v-if="showClientIp" class="meta-item">
            <div class="meta-item__label">IP</div>
            <div class="meta-item__value">
              <a
                v-if="current.ClientIp"
                href="javascript:;"
                class="ip-link"
                @click="openIpLocation(current.ClientIp)"
              >{{ current.ClientIp }}</a>
              <span v-else class="muted">—</span>
            </div>
          </div>
          <div class="meta-item meta-item--wide">
            <div class="meta-item__label">接口</div>
            <div class="meta-item__value mono-cell">{{ current.Path || '—' }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">业务动作</div>
            <div class="meta-item__value">{{ current.BizAction || current.ActionName || '—' }}</div>
          </div>
          <div class="meta-item meta-item--wide">
            <div class="meta-item__label">TraceId</div>
            <div class="meta-item__value mono-cell">{{ current.TraceId || '—' }}</div>
          </div>
        </div>

        <div class="audit-summary" :class="{ 'audit-summary--failed': !current.Success }">
          <div class="audit-summary__head">
            <div class="audit-summary__head-left">
              <i :class="current.Success ? 'el-icon-circle-check' : 'el-icon-circle-close'" />
              <span>结果说明</span>
            </div>
            <el-button
              type="text"
              class="payload-copy"
              icon="el-icon-document-copy"
              @click="copyText(current.ResultMsg || '', '结果说明')"
            >复制</el-button>
          </div>

          <template v-if="resultParsed.hasStructure">
            <div class="audit-summary__outcome">
              <el-tag
                size="mini"
                effect="plain"
                :type="(resultParsed.outcome === '失败' || !current.Success) ? 'danger' : 'success'"
              >{{ resultParsed.outcome || (current.Success ? '成功' : '失败') }}</el-tag>
              <span v-if="resultParsed.outcomeDetail" class="audit-summary__outcome-msg">{{ resultParsed.outcomeDetail }}</span>
            </div>

            <div v-if="resultParsed.title" class="audit-summary__title">{{ resultParsed.title }}</div>

            <div v-if="resultParsed.fields.length" class="audit-summary__kv">
              <div v-for="(f, idx) in resultParsed.fields" :key="idx" class="audit-kv">
                <div class="audit-kv__k" :title="f.key">{{ f.key }}</div>
                <div class="audit-kv__v" :title="f.value">{{ f.value }}</div>
              </div>
            </div>
          </template>
          <div v-else class="audit-summary__value">{{ current.ResultMsg || '未返回结果说明' }}</div>
        </div>

        <div class="payload-block">
          <div class="payload-block__head">
            <span>Query</span>
            <el-button type="text" class="payload-copy" icon="el-icon-document-copy" @click="copyText(current.QueryString || '', 'Query')">复制</el-button>
          </div>
          <pre class="payload-block__body">{{ current.QueryString || '（空）' }}</pre>
        </div>
        <div class="payload-block">
          <div class="payload-block__head">
            <span>RequestBody</span>
            <el-button type="text" class="payload-copy" icon="el-icon-document-copy" @click="copyText(bodyText(current.RequestBody, 'request'), 'RequestBody')">复制</el-button>
          </div>
          <pre class="payload-block__body">{{ bodyText(current.RequestBody, 'request') }}</pre>
        </div>
        <div class="payload-block">
          <div class="payload-block__head">
            <span>ResponseBody</span>
            <el-button type="text" class="payload-copy" icon="el-icon-document-copy" @click="copyText(bodyText(current.ResponseBody, 'response'), 'ResponseBody')">复制</el-button>
          </div>
          <pre class="payload-block__body">{{ bodyText(current.ResponseBody, 'response') }}</pre>
        </div>
        <div class="payload-block">
          <div class="payload-block__head">
            <span>UserAgent</span>
            <el-button type="text" class="payload-copy" icon="el-icon-document-copy" @click="copyText(current.UserAgent || '', 'UserAgent')">复制</el-button>
          </div>
          <pre class="payload-block__body">{{ current.UserAgent || '—' }}</pre>
        </div>
      </template>

      <div slot="footer" class="dialog-footer">
        <el-button type="primary" plain icon="el-icon-document-copy" @click="copyDetailAll">复制全部</el-button>
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="ipLocVisible"
      width="420px"
      custom-class="log-detail-dialog ip-loc-dialog"
      :close-on-click-modal="true"
      append-to-body
    >
      <div slot="title" class="log-detail-dialog__title">
        <span>IP 归属地</span>
      </div>
      <div v-loading="ipLocLoading" class="ip-loc-dialog__body">
        <div class="ip-loc-card">
          <div class="ip-loc-card__label">IP 地址</div>
          <div class="ip-loc-card__value mono-cell">{{ ipLocCurrent.Ip || ipLocQueryIp || '—' }}</div>
        </div>
        <div class="ip-loc-card">
          <div class="ip-loc-card__label">归属地</div>
          <div class="ip-loc-card__value">{{ ipLocCurrent.Location || (ipLocLoading ? '查询中…' : '—') }}</div>
        </div>
        <div v-if="ipLocCurrent.Isp" class="ip-loc-card">
          <div class="ip-loc-card__label">运营商 / ISP</div>
          <div class="ip-loc-card__value">{{ ipLocCurrent.Isp }}</div>
        </div>
        <div v-if="ipLocCurrent.IsPrivate" class="ip-loc-hint">
          内网或本机地址无法解析公网地理位置。
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button
          v-if="ipLocCurrent.ExternalQueryUrl"
          type="primary"
          plain
          icon="el-icon-link"
          @click="openExternalIpQuery"
        >外部查询</el-button>
        <el-button @click="ipLocVisible = false">关闭</el-button>
      </div>
    </el-dialog>
    </section>
  </section>
</template>

<script>
import {
  getOperationAuditPage,
  getOperationAuditDetail,
  getOperationAuditQueueHealth,
  getIpLocation
} from '../../api/modules/operationAudit';

const MAX_RANGE_DAYS = 7;

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  // 结束默认当天 24 点（23:59:59），避免卡在打开页那一刻。最后更新：2026-07-24
  end.setHours(23, 59, 59, 0);
  const fmt = (d) => {
    const p = (n) => (n < 10 ? '0' + n : '' + n);
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  };
  return [fmt(start), fmt(end)];
}

export default {
  name: 'OperationAuditLog',
  data() {
    return {
      maxRangeDays: MAX_RANGE_DAYS,
      filters: {
        scope: 'write',
        dateRange: defaultRange(),
        keyword: '',
        success: null,
        minDurationMs: null
      },
      users: [],
      total: 0,
      page: 1,
      pageSize: 10,
      listLoading: false,
      detailVisible: false,
      detailLoading: false,
      current: null,
      queueHealth: {},
      queueHealthTimer: null,
      // 临时隐藏 IP 列/详情（改 true 可恢复）。最后更新：2026-07-24
      showClientIp: false,
      ipLocVisible: false,
      ipLocLoading: false,
      ipLocQueryIp: '',
      ipLocCurrent: {},
      ipLocCache: {}
    };
  },
  mounted() {
    this.handleQuery();
    this.loadQueueHealth();
    this.queueHealthTimer = window.setInterval(this.loadQueueHealth, 30000);
  },
  beforeDestroy() {
    if (this.queueHealthTimer) window.clearInterval(this.queueHealthTimer);
  },
  computed: {
    queueHealthClass() {
      if ((this.queueHealth.Failed || 0) > 0 || (this.queueHealth.Dropped || 0) > 0) return 'queue-health--bad';
      const capacity = this.queueHealth.Capacity || 1;
      if ((this.queueHealth.Pending || 0) / capacity >= 0.6) return 'queue-health--warn';
      return 'queue-health--ok';
    },
    /** 解析结果说明为标题 / 键值 / 结果摘要。最后修改：2026-08-07 */
    resultParsed() {
      return this.parseResultMsg(this.current && this.current.ResultMsg);
    }
  },
  methods: {
    /**
     * 将审计 ResultMsg（「动作：k=v；…；结果=成功：msg」）拆成可读结构。创建于 2026-08-07
     */
    parseResultMsg(raw) {
      const empty = { hasStructure: false, title: '', outcome: '', outcomeDetail: '', fields: [] };
      if (!raw || !String(raw).trim()) return empty;

      const text = String(raw).trim();
      const parts = text.split(/[；;]/).map((s) => s.trim()).filter(Boolean);
      if (parts.length === 0) return empty;

      let title = '';
      let outcome = '';
      let outcomeDetail = '';
      const fields = [];

      const pushField = (key, value) => {
        const k = (key || '').trim();
        const v = (value == null ? '' : String(value)).trim();
        if (!k && !v) return;
        fields.push({ key: k || '说明', value: v || '—' });
      };

      const parseKv = (segment) => {
        const eq = segment.indexOf('=');
        if (eq > 0) {
          pushField(segment.slice(0, eq), segment.slice(eq + 1));
          return true;
        }
        return false;
      };

      parts.forEach((part, index) => {
        const resultMatch = part.match(/^结果\s*=\s*(成功|失败)(?:\s*[：:]\s*(.*))?$/);
        if (resultMatch) {
          outcome = resultMatch[1];
          outcomeDetail = (resultMatch[2] || '').trim();
          return;
        }

        if (index === 0) {
          const colonIdx = Math.max(part.indexOf('：'), part.indexOf(':'));
          if (colonIdx > 0) {
            title = part.slice(0, colonIdx).trim();
            const rest = part.slice(colonIdx + 1).trim();
            if (rest) {
              if (!parseKv(rest)) pushField('说明', rest);
            }
            return;
          }
          if (!part.includes('=')) {
            title = part;
            return;
          }
        }

        if (!parseKv(part)) {
          if (!title) title = part;
          else pushField('说明', part);
        }
      });

      const hasStructure = !!(title || fields.length || outcome || outcomeDetail);
      return { hasStructure, title, outcome, outcomeDetail, fields };
    },
    loadQueueHealth() {
      getOperationAuditQueueHealth()
        .then((res) => {
          this.queueHealth = (res.data && res.data.response) || {};
        })
        .catch(() => {});
    },
    /**
     * 打开 IP 归属地弹窗。创建于 2026-07-23
     */
    openIpLocation(ip) {
      if (!ip) return;
      this.ipLocQueryIp = ip;
      this.ipLocCurrent = this.ipLocCache[ip] || {};
      this.ipLocVisible = true;
      this.loadIpLocation(ip);
    },
    /**
     * 加载 IP 归属地（带前端缓存）。创建于 2026-07-23
     */
    loadIpLocation(ip) {
      if (!ip) return;
      if (this.ipLocCache[ip]) {
        this.ipLocCurrent = this.ipLocCache[ip];
        return;
      }
      this.ipLocLoading = true;
      getIpLocation(ip)
        .then((res) => {
          const data = (res.data && res.data.response) || null;
          const payload = data || {
            Ip: ip,
            Location: (res.data && res.data.msg) || '查询失败',
            Isp: '',
            IsPrivate: false,
            ExternalQueryUrl: 'https://www.ip.cn/ip/' + encodeURIComponent(ip) + '.html'
          };
          this.$set(this.ipLocCache, ip, payload);
          if (this.ipLocQueryIp === ip) {
            this.ipLocCurrent = payload;
          }
        })
        .catch(() => {
          const payload = {
            Ip: ip,
            Location: '查询失败',
            Isp: '',
            IsPrivate: false,
            ExternalQueryUrl: 'https://www.ip.cn/ip/' + encodeURIComponent(ip) + '.html'
          };
          this.$set(this.ipLocCache, ip, payload);
          if (this.ipLocQueryIp === ip) {
            this.ipLocCurrent = payload;
          }
        })
        .finally(() => {
          this.ipLocLoading = false;
        });
    },
    openExternalIpQuery() {
      const url = this.ipLocCurrent && this.ipLocCurrent.ExternalQueryUrl;
      if (url) window.open(url, '_blank');
    },
    bodyText(v, kind) {
      if (!v && kind === 'response' && this.current && this.current.Success &&
          ['GET', 'HEAD'].indexOf((this.current.HttpMethod || '').toUpperCase()) >= 0) {
        return '（按策略未保存：成功只读请求仅记录状态、耗时和结果摘要）';
      }
      if (!v) return '（正文为空或该接口仅采集元数据）';
      try {
        return JSON.stringify(JSON.parse(v), null, 2);
      } catch (e) {
        return v;
      }
    },
    /**
     * 复制文本到剪贴板。最后更新：2026-07-22
     */
    copyText(text, label) {
      const value = text == null ? '' : String(text);
      if (!value || value === '—' || value === '（空）' || value.indexOf('（空') === 0) {
        this.$message.warning((label || '内容') + '为空，无需复制');
        return;
      }
      const done = () => this.$message.success((label || '内容') + '已复制');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => this.fallbackCopy(value, done));
        return;
      }
      this.fallbackCopy(value, done);
    },
    fallbackCopy(text, done) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', 'readonly');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        if (typeof done === 'function') done();
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本');
      }
      document.body.removeChild(ta);
    },
    /**
     * 复制当前审计详情摘要与正文。最后更新：2026-07-22
     */
    copyDetailAll() {
      if (!this.current) return;
      const c = this.current;
      const lines = [
        `时间: ${c.StartTime || ''}`,
        `操作人: ${c.UserName || ''}`,
        `方法: ${c.HttpMethod || ''}`,
        `耗时: ${c.DurationMs}ms`,
        `HTTP: ${c.StatusCode}`,
        ...(this.showClientIp ? [`IP: ${c.ClientIp || ''}`] : []),
        `接口: ${c.Path || ''}`,
        `业务动作: ${c.BizAction || c.ActionName || ''}`,
        `操作摘要: ${c.ResultMsg || ''}`,
        `TraceId: ${c.TraceId || ''}`,
        `结果: ${c.Success ? '成功' : '失败'}`,
        '',
        '--- Query ---',
        c.QueryString || '（空）',
        '',
        '--- RequestBody ---',
        this.bodyText(c.RequestBody, 'request'),
        '',
        '--- ResponseBody ---',
        this.bodyText(c.ResponseBody, 'response'),
        '',
        '--- UserAgent ---',
        c.UserAgent || '—'
      ];
      this.copyText(lines.join('\n'), '审计详情');
    },
    durationClass(ms) {
      if (ms == null) return '';
      if (ms >= 1000) return 'is-slow';
      if (ms >= 300) return 'is-warn';
      return 'is-ok';
    },
    statusClass(code) {
      if (code >= 200 && code < 300) return 'is-ok';
      if (code >= 400) return 'is-bad';
      return '';
    },
    handleCurrentChange(val) {
      this.page = val;
      this.handleQuery();
    },
    handleSearch() {
      this.page = 1;
      this.handleQuery();
    },
    handleReset() {
      this.filters = {
        scope: 'write',
        dateRange: defaultRange(),
        keyword: '',
        success: null,
        minDurationMs: null
      };
      this.handleSearch();
    },
    ensureRangeOk(dateRange) {
      if (!dateRange || dateRange.length !== 2) {
        this.$message.warning('请选择查询时间');
        return false;
      }
      const start = new Date(String(dateRange[0]).replace(/-/g, '/')).getTime();
      const end = new Date(String(dateRange[1]).replace(/-/g, '/')).getTime();
      if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
        this.$message.warning('时间范围无效');
        return false;
      }
      if ((end - start) / (24 * 3600 * 1000) > MAX_RANGE_DAYS) {
        this.$message.warning(`查询时间跨度不能超过 ${MAX_RANGE_DAYS} 天`);
        return false;
      }
      return true;
    },
    handleQuery() {
      const dateRange = this.filters.dateRange;
      if (!this.ensureRangeOk(dateRange)) return;

      const para = {
        page: this.page,
        pageSize: this.pageSize,
        startTime: dateRange[0],
        endTime: dateRange[1],
        keyword: this.filters.keyword || undefined,
        writeOnly: this.filters.scope === 'write',
        knownTotal: this.page > 1 ? this.total : undefined,
        success:
          this.filters.success === '' || this.filters.success === undefined || this.filters.success === null
            ? undefined
            : this.filters.success,
        minDurationMs:
          this.filters.minDurationMs === '' ||
          this.filters.minDurationMs === undefined ||
          this.filters.minDurationMs === null
            ? undefined
            : this.filters.minDurationMs
      };

      this.listLoading = true;
      getOperationAuditPage(para)
        .then((res) => {
          const pageData = (res.data && res.data.response) || {};
          this.total = pageData.dataCount || 0;
          this.users = pageData.data || [];
        })
        .catch(() => {
          this.$message.error('查询失败');
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    showDetail(row) {
      this.current = { ...row };
      this.detailVisible = true;
      this.detailLoading = true;
      getOperationAuditDetail(row.Id, row.StartTime)
        .then((res) => {
          const detail = (res.data && res.data.response) || null;
          if (detail) this.current = detail;
        })
        .catch(() => {
          this.$message.error('审计详情加载失败');
        })
        .finally(() => {
          this.detailLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.log-page {
  --log-ink: #0f172a;
  --log-muted: #64748b;
  --log-line: #e2e8f0;
  --log-panel: #ffffff;
  --log-soft: #f8fafc;
  --log-ok: #15803d;
  --log-warn: #b45309;
  --log-bad: #b91c1c;
  color: var(--log-ink);
}

.log-page__head {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.log-page__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
}

.queue-health {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--log-line);
  border-radius: 6px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  cursor: pointer;
}

.queue-health i {
  margin-right: 5px;
}

.queue-health--ok i { color: var(--log-ok); }
.queue-health--warn i { color: var(--log-warn); }
.queue-health--bad i { color: var(--log-bad); }

.log-page__desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--log-muted);
  line-height: 1.5;
}

.log-table >>> th {
  background: var(--log-soft) !important;
  color: #334155 !important;
  font-weight: 600;
}

.log-table >>> .el-table__row:hover > td {
  background: #f1f5f9 !important;
}

.mono-cell {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  color: #334155;
}

.ip-link {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  color: #2563eb;
  text-decoration: none;
  border-bottom: 1px dashed rgba(37, 99, 235, 0.45);
  cursor: pointer;
}

.ip-link:hover {
  color: #1d4ed8;
  border-bottom-color: #1d4ed8;
}

.col-header-tip {
  margin-left: 2px;
  color: #94a3b8;
  cursor: help;
  font-size: 13px;
  vertical-align: middle;
}

.col-header-tip:hover {
  color: #64748b;
}

.ip-loc-dialog__body {
  min-height: 120px;
}

.ip-loc-card {
  padding: 12px 14px;
  border: 1px solid var(--log-line);
  border-radius: 8px;
  background: var(--log-soft);
  margin-bottom: 10px;
}

.ip-loc-card:last-of-type {
  margin-bottom: 0;
}

.ip-loc-card__label {
  font-size: 12px;
  color: var(--log-muted);
  margin-bottom: 4px;
}

.ip-loc-card__value {
  font-size: 14px;
  color: var(--log-ink);
  line-height: 1.5;
  word-break: break-all;
}

.ip-loc-hint {
  margin-top: 12px;
  font-size: 12px;
  color: var(--log-muted);
  line-height: 1.5;
}

.muted {
  color: #94a3b8;
}

.method-pill {
  display: inline-block;
  min-width: 48px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  background: #e2e8f0;
  color: #334155;
}

.method-pill--get {
  background: #dbeafe;
  color: #1d4ed8;
}

.method-pill--post {
  background: #dcfce7;
  color: #15803d;
}

.method-pill--put,
.method-pill--patch {
  background: #ffedd5;
  color: #c2410c;
}

.method-pill--delete {
  background: #fee2e2;
  color: #b91c1c;
}

.is-ok {
  color: var(--log-ok);
  font-weight: 600;
}

.is-warn {
  color: var(--log-warn);
  font-weight: 600;
}

.is-slow,
.is-bad {
  color: var(--log-bad);
  font-weight: 600;
}

.detail-link {
  cursor: pointer;
  font-weight: 500;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 16px;
  margin-bottom: 14px;
  padding: 12px 14px;
  background: #fafbfc;
  border: 1px solid #eef2f6;
  border-radius: 8px;
}

.meta-item {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 2px 0;
}

.meta-item--wide {
  grid-column: span 2;
}

.meta-item__label {
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 2px;
  letter-spacing: 0.02em;
}

.meta-item__value {
  font-size: 13px;
  color: #334155;
  word-break: break-all;
  line-height: 1.45;
}

.audit-summary {
  margin: 0 0 12px;
  padding: 10px 12px 12px;
  border: 1px solid #e8edf2;
  border-left: 3px solid var(--log-ok);
  border-radius: 0 8px 8px 0;
  background: #f8fafc;
}

.audit-summary__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.audit-summary__head-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.audit-summary__head-left i {
  color: var(--log-ok);
  font-size: 14px;
}

.audit-summary__outcome {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.audit-summary__outcome-msg {
  flex: 1;
  min-width: 140px;
  color: #334155;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}

.audit-summary__title {
  margin-bottom: 8px;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.audit-summary__kv {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
}

.audit-kv {
  display: grid;
  grid-template-columns: minmax(72px, 38%) minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  padding: 6px 8px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #eef2f6;
  min-width: 0;
}

.audit-kv__k {
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.45;
  word-break: break-all;
}

.audit-kv__v {
  color: #334155;
  font-size: 12px;
  line-height: 1.45;
  font-family: Consolas, 'Courier New', monospace;
  word-break: break-all;
}

.audit-summary__value {
  color: #334155;
  font-size: 13px;
  line-height: 1.7;
  word-break: break-word;
  white-space: pre-wrap;
}

.audit-summary--failed {
  border-left-color: var(--log-bad);
}

.audit-summary--failed .audit-summary__head-left i {
  color: var(--log-bad);
}

@media (max-width: 640px) {
  .audit-summary__kv {
    grid-template-columns: 1fr;
  }
}

.payload-block {
  border: 1px solid #e8edf2;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
  background: #fff;
}

.payload-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px 6px 12px;
  background: #f7f9fb;
  border-bottom: 1px solid #eef2f6;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.payload-copy {
  padding: 0 4px !important;
  font-size: 12px !important;
  color: #64748b !important;
  cursor: pointer;
}

.payload-copy:hover {
  color: var(--btn-primary, #2563eb) !important;
}

.payload-block__body {
  margin: 0;
  padding: 10px 12px;
  max-height: 180px;
  overflow: auto;
  background: #f8fafc;
  color: #475569;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.log-detail-dialog__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #334155;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 900px) {
  .meta-grid {
    grid-template-columns: 1fr 1fr;
  }

  .meta-item--wide {
    grid-column: span 2;
  }
}
</style>

<style>
.log-detail-dialog .el-dialog__body {
  padding-top: 12px;
}
</style>
